import React, { useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  type ColumnsStylesInterface,
  type GridExcelExportOptions,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import {
  ArrowDownToLine,
  ChevronLeft,
  Dot,
  Info,
  LockKeyhole,
} from 'lucide-react';
import { Pencil } from 'lucide-react';

import Button from '../../common/Button';
import {
  useGetDataTableByName,
  useGetReceivedModelDataTableByName,
  useGetSharedModelDataTableByName,
} from '../../hooks/tanstack/useDataTablesHook';
import {
  useActivateBaseScenario,
  useActivateScenario,
  useGetBaseScenario,
  useGetBaseScenarioData,
  useGetScenarioById,
  useGetScenarioData,
  useGetScenarioVectors,
} from '../../hooks/tanstack/useScenariosHook';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import DataGridPremiumTable from '../DataGridPremiumTable';
import CreateScenarioFormDialog from '../scenario/CreateScenarioFormDialog';
import VectorsSection from '../scenario/VectorsSection';
import CreateVectorFormDialog from '../vector/CreateVectorFormDialog';

const ModelScenarioDetailView = () => {
  const { orgId, projectId, inputTableName, modelId, scenarioId } =
    useGetRouteParams();

  const apiRef = useGridApiRef();

  const navigate = useNavigate();

  const [openDialogCreateVector, setOpenDialogCreateVector] = useState(false);
  const [openDialogEditScenario, setOpenDialogEditScenario] = useState(false);

  const { model, type, isReceived, isShared } = useGetDistinguishModel();

  const form = useForm({
    defaultValues: {
      aggregateColumn: '',
    },
  });

  const { data: vectors } = useGetScenarioVectors(
    {
      scenarioId,
      orgId,
      projectId,
      modelId,
    },
    {
      enabled: !!scenarioId && !!orgId && !!projectId && !!modelId,
    }
  );

  const activeVector = vectors?.find((v) => v.is_active);

  const { data: defaultDataTable } = useGetDataTableByName(
    {
      dataTableName: inputTableName ?? '',
      orgId,
      projectId,
      modelId,
    },
    {
      enabled: !!inputTableName && !!orgId && !!projectId && !!modelId && !type,
    }
  );

  const { data: sharedDataTable } = useGetSharedModelDataTableByName(
    {
      dataTableName: inputTableName ?? '',
      orgId,
      projectId,
      modelId,
    },
    {
      enabled:
        !!inputTableName &&
        !!orgId &&
        !!projectId &&
        !!modelId &&
        type === 'shared',
    }
  );

  const { data: receivedDataTable } = useGetReceivedModelDataTableByName(
    {
      dataTableName: inputTableName ?? '',
      orgId,
      projectId,
      modelId,
    },
    {
      enabled:
        !!inputTableName &&
        !!orgId &&
        !!projectId &&
        !!modelId &&
        type === 'received',
    }
  );

  const dataTable = sharedDataTable || defaultDataTable || receivedDataTable;

  const { data: scenario, isLoading: isLoadingScenario } = useGetScenarioById(
    {
      scenarioId,
      orgId,
      projectId,
      modelId,
    },
    {
      enabled:
        !!scenarioId && !!orgId && !!projectId && !!modelId && scenarioId !== 0,
    }
  );

  const { data: baseScenario, isLoading: isLoadingBaseScenario } =
    useGetBaseScenario(
      {
        orgId,
        modelId,
        projectId,
        dataTableName: inputTableName ?? '',
      },
      {
        enabled:
          !!orgId &&
          !!modelId &&
          !!projectId &&
          !!inputTableName &&
          scenarioId === 0,
      }
    );

  const {
    data: scenarioData,
    isLoading: isGenerateScenarioData,
    refetch: refetchGenerateScenarioData,
    isRefetching: isRefetchingScenarioData,
  } = useGetScenarioData(
    {
      scenarioId,
      orgId,
      modelId,
      projectId,
    },
    {
      enabled: !!scenarioId && !!orgId && !!projectId && !!modelId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: baseScenarioData,
    isLoading: isGenerateBaseScenarioData,
    isRefetching: isRefetchingBaseScenarioData,
  } = useGetBaseScenarioData(
    {
      orgId,
      modelId,
      projectId,
      dataTableName: inputTableName ?? '',
    },
    {
      enabled: !!inputTableName && !!orgId && !!projectId && !!modelId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: activateScenario, isPending: isActivatingScenario } =
    useActivateScenario();

  const { mutate: activateBaseScenario, isPending: isActivatingBaseScenario } =
    useActivateBaseScenario();

  const handleExportToExcel = () => {
    const colStyles: ColumnsStylesInterface = {};

    dataTable?.columns.forEach((colDef) => {
      colStyles[colDef.name] = {
        numFmt: colDef.name === 'employee_id' ? '#,##' : '#,##0.00',
        font: {},
        alignment: {},
        protection: {},
        border: {},
        fill: {
          type: 'pattern',
          pattern: 'none',
        },
      };
    });

    const options: GridExcelExportOptions = {
      fileName: getExportFileName(),
      columnsStyles: { ...colStyles },
    };

    apiRef.current.exportDataAsExcel(options);
  };

  const handleGoBack = () => {
    navigate(
      `/${orgId}/projects/${projectId}/models/${modelId}?tab=scenario&type=${type}`
    );
  };

  const handleOpenDialogEditScenario = () => {
    setOpenDialogEditScenario(true);
  };

  const handleCloseDialogEditScenario = () => {
    setOpenDialogEditScenario(false);
  };

  const getExportFileName = () => {
    const modelName = model ? model.name : 'model_unknown';
    const dataTableName = dataTable ? dataTable.name : 'table_unknown';
    const scenarioName = scenario ? scenario.name : 'scenario_unknown';

    return modelName + '_' + dataTableName + '_' + scenarioName;
  };

  const handleActivateScenario = () => {
    if (!inputTableName) {
      return;
    }

    if (scenarioId === 0) {
      activateBaseScenario({
        orgId,
        projectId,
        modelId,
        dataTableName: inputTableName,
      });
      return;
    }

    activateScenario({
      orgId,
      projectId,
      modelId,
      scenarioId,
      dataTableName: inputTableName,
    });
  };

  const scenarioInfo = useMemo(
    () =>
      scenarioId === 0
        ? {
            ...baseScenario,
            is_base: true,
          }
        : scenario,
    [scenarioId, scenario, baseScenario]
  );

  const scenarioBase = useMemo(
    () =>
      !scenarioInfo?.is_base && activeVector
        ? {
            data: baseScenarioData,
            mode: 'vector' as 'vector',
          }
        : undefined,
    [scenarioInfo, baseScenarioData, activeVector]
  );

  const isActivating = isActivatingScenario || isActivatingBaseScenario;

  const isGenerating =
    isGenerateScenarioData ||
    isGenerateBaseScenarioData ||
    isRefetchingScenarioData ||
    isRefetchingBaseScenarioData;

  const isLoading = isLoadingScenario || isLoadingBaseScenario;

  return (
    <FormProvider {...form}>
      <form>
        <div className="flex flex-col space-y-6 px-12">
          <div className="flex justify-between">
            <div className="flex items-center gap-2">
              <ChevronLeft
                onClick={handleGoBack}
                className="cursor-pointer"
                size={25}
                color="#333333"
              />
              {(isReceived || isShared) && (
                <LockKeyhole size={24} color="#333333" />
              )}
              <h1 className="text-2xl font-bold">{scenarioInfo?.name}</h1>
              {!isLoading && !scenarioInfo?.is_base && !model?.is_locked && (
                <Pencil
                  size={20}
                  color="#333333"
                  className="cursor-pointer"
                  onClick={handleOpenDialogEditScenario}
                />
              )}
            </div>
            {scenarioInfo?.is_active ? (
              <div className="px-2 pr-4 flex items-center bg-[#E1FBE7] rounded-full">
                <Dot color="#0B9919" />
                <p className="text-sm text-[#0B9919] font-bold">Active</p>
              </div>
            ) : (
              <Button
                type="button"
                loading={isActivating}
                onClick={handleActivateScenario}
                disabled={isActivating || isGenerating}
                variant="contained_blue"
              >
                Use this scenario
              </Button>
            )}
          </div>
          {!scenarioInfo?.is_base && (
            <div className="flex flex-col gap-3 rounded-[4px] border border-[#E4E7EC] bg-[#FFF] px-6 py-4">
              <h2 className="font-bold">Description</h2>
              <p>{scenarioInfo?.description}</p>
            </div>
          )}
          {!scenarioInfo?.is_base && dataTable?.is_pivot && (
            <div className="space-y-3">
              <div className="flex justify-between">
                <h2 className="text-xl font-bold">Vectors</h2>
                {!model.is_locked && (
                  <CreateVectorFormDialog
                    open={openDialogCreateVector}
                    title="Add new vector"
                    handleCloseDialog={() => {
                      setOpenDialogCreateVector(false);
                    }}
                    handleOpenDialog={() => {
                      setOpenDialogCreateVector(true);
                    }}
                  />
                )}
              </div>
              {dataTable.is_pivot ? (
                <VectorsSection
                  handleOnActivate={refetchGenerateScenarioData}
                />
              ) : (
                <div className="w-full text-center text-gray-500">
                  This input data table doesn't support vectors
                </div>
              )}
            </div>
          )}
          <div className="space-y-4">
            <div className="flex justify-between">
              <div className="flex flex-col gap-2 w-[80%] justify-end">
                <h2 className="text-xl font-bold">Data Preview</h2>
                <div className="flex gap-2 w-full items-center">
                  <div>
                    <Info size={20} color="#666666" />
                  </div>
                  <p className=" text-[#666666] font-normal">
                    Preview may not be 100% accurate when using custom expense
                    formula. The table below is computed with the latest model
                    run output. Run the model again to see the actual output.
                  </p>
                </div>
              </div>
              <div className="flex justify-end items-end">
                <div>
                  <Button
                    onClick={handleExportToExcel}
                    disabled={isLoading}
                    variant="outlined_blue"
                  >
                    <ArrowDownToLine />
                    <label className="font-semibold capitalize">
                      Export to Excel
                    </label>
                  </Button>
                </div>
              </div>
            </div>
            <DataGridPremiumTable
              apiRef={apiRef}
              columns={
                dataTable?.columns?.map((c) => ({
                  label: c.display_name,
                  name: c.name,
                  is_pinned: c.is_pinned,
                  data_type: c.data_type,
                  styles: c.styles,
                })) ?? []
              }
              base={scenarioBase}
              isLoading={isGenerating}
              data={scenarioId === 0 ? baseScenarioData : scenarioData}
            />
          </div>
        </div>
        {scenario && (
          <CreateScenarioFormDialog
            title="Edit Scenario"
            showTrigger={false}
            defaultValues={{
              id: scenario.id,
              name: scenario.name,
              description: scenario.description,
            }}
            dataTableDisplayName={dataTable?.display_name}
            open={openDialogEditScenario}
            handleCloseDialog={handleCloseDialogEditScenario}
            handleOpenDialog={handleOpenDialogEditScenario}
          />
        )}
      </form>
    </FormProvider>
  );
};

export default ModelScenarioDetailView;
